import { useReactiveVar } from '@apollo/client';
import Drawer from 'components/Drawer/Drawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import usePermissions from 'hooks/usePermissions/usePermissions';
import useSortingPermissionsTable from 'hooks/useSorting/useSortingPermissionsTable/useSortingPermissionsTable';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { kebabCase } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { PermissionsState } from 'state/types';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import Search from 'ui/molecules/Search/Search';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import ArrayUtil from 'utils/Array/Array.util';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import getVisitorGroupIcon from 'utils/VisitorGroupIcon/VisitorGroupIcon.util';
import { createSettingsTabs } from '../settingsTabsConst';

const SearchWrapper = styled.div`
  margin: 1rem 0 2rem 0;
  width: 14.875rem;
`;

export const createHeaderLeftTable = (translation: Translation) => [translation.ad_groups];
export const createHeaderRightTable = (translation: Translation) => [translation.permissions];

const Permissions: React.FC = () => {
  const isAllowQrInvitesEnabled = process.env.REACT_APP_IS_ALLOW_QR_INVITES_ENABLED === 'true';
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const isEntraLess = user?.isEntraLess!;
  const settingsTabs = createSettingsTabs(translation, isEntraLess);
  const headerLeftTable = createHeaderLeftTable(translation);
  const headerRightTable = createHeaderRightTable(translation);
  const [filterStr, setFilterStr] = useState('');
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const skeletonArray = ArrayUtil.SkeletonArray();
  const {
    groupsWithPermissions,
    loading: visitorGroupsLoading,
    setIsAdminGroup,
    setIsAdminGroupLoading,
    setIsQrCodeVisitInviterGroup,
    setIsQrCodeVisitInvitorGroupLoading
  } = usePermissions({
    handleFetchError
  });
  const [leftTableSelectedRowGroupId, setLeftTableSelectedRowGroupId] = useState<string>();
  const { handleSortingIcon, handleSortingOnClick } = useSortingPermissionsTable(translation);

  const getFilterData = useCallback((): PermissionsState => {
    if (visitorGroupsLoading) return groupsWithPermissions;
    return filterBySearchTerm(groupsWithPermissions, filterStr, (group) => [group.name]);
  }, [filterStr, groupsWithPermissions, visitorGroupsLoading]);

  const handleLeftTableOnRowClick = (groupId: string) => {
    if (leftTableSelectedRowGroupId !== groupId) setLeftTableSelectedRowGroupId(groupId);
    else setLeftTableSelectedRowGroupId(undefined);
  };

  const IsAdminGroup = (groupId: string | undefined) =>
    groupsWithPermissions.find((group) => group.id === groupId)?.isAdminGroup;

  const IsQrCodeVisitInviterGroup = (groupId: string | undefined) =>
    groupsWithPermissions.find((group) => group.id === groupId)?.isQrCodeVisitInviterGroup;

  const handleRightTableOnIsAdminRowClick = () => {
    if (leftTableSelectedRowGroupId !== undefined && !setIsAdminGroupLoading) {
      setIsAdminGroup(leftTableSelectedRowGroupId, !IsAdminGroup(leftTableSelectedRowGroupId));
    }
  };

  const handleRightTableOnIsQrCodeVisitInviterRowClick = () => {
    if (leftTableSelectedRowGroupId !== undefined && !setIsQrCodeVisitInvitorGroupLoading) {
      setIsQrCodeVisitInviterGroup(
        leftTableSelectedRowGroupId,
        !IsQrCodeVisitInviterGroup(leftTableSelectedRowGroupId)
      );
    }
  };

  useEffect(() => {
    if (
      !visitorGroupsLoading &&
      filterStr &&
      leftTableSelectedRowGroupId &&
      getFilterData().find((item) => item.id === leftTableSelectedRowGroupId) === undefined
    )
      setLeftTableSelectedRowGroupId(undefined);
  }, [filterStr, getFilterData, leftTableSelectedRowGroupId, visitorGroupsLoading]);

  return (
    <PageContent title="ATLAS" titleId="location" tabs={settingsTabs} id="permissions-page">
      <StickyToTop>
        <ComponentWrapper alignItems="center" justifyContent="end" gap="0.5rem">
          <SearchWrapper>
            <Search onChange={(event) => setFilterStr(event.target.value)} value={filterStr} />
          </SearchWrapper>
        </ComponentWrapper>
      </StickyToTop>
      <ComponentWrapper width="100%" height="calc(100vh - 24rem)" margin="0 1rem 0 0" gap="1rem">
        <Table
          header={
            <TableHeaderRow placeForAvatarOrSwitchOrCheckbox placeForArrow>
              <TableHeader
                headerText={headerLeftTable[0]}
                id={kebabCase(`header-${headerLeftTable[0]}`)}
                flex="0 0 100%"
                iconSorting={handleSortingIcon(headerLeftTable[0])}
                onClick={() => handleSortingOnClick(headerLeftTable[0])}
              />
            </TableHeaderRow>
          }
          headerId="ad-groups-header"
          rowsWrapperId="ad-groups-list"
        >
          {visitorGroupsLoading
            ? skeletonArray.map((_, index) => (
                <TableRow
                  beforeSwitchSkeleton
                  id={`row-skeleton-${index}`}
                  marginAfterContent
                  key={`skeletonTableRowLeftColumn-${_.id}`}
                >
                  <TableCell isLoading firstLineText="" flex="0 0 100%" />
                </TableRow>
              ))
            : getFilterData().map((userGroup, id) => (
                <TableRow
                  hoverEffect
                  beforeContentIconProps={{
                    name: getVisitorGroupIcon.getVisitorGroupIcon(userGroup.type as VisitorGroupType),
                    width: 24,
                    height: 24,
                    viewBox: '0 0 24 24',
                    stroke: 'transparent',
                    color: 'lTextHigh'
                  }}
                  id={`row-${id}`}
                  key={userGroup.id}
                  onClick={() => handleLeftTableOnRowClick(userGroup.id)}
                  selected={userGroup.id === leftTableSelectedRowGroupId}
                >
                  <TableCell firstLineText={userGroup.name} firstLineId={`row-${id}-name`} flex="0 0 100%" />
                </TableRow>
              ))}
        </Table>
        <Table
          header={
            <TableHeaderRow>
              <TableHeader
                headerText={headerRightTable[0]}
                id={`header-${headerRightTable[0].toLocaleLowerCase()}`}
                flex="0 0 100%"
              />
            </TableHeaderRow>
          }
          headerId="ad-groups-header"
          rowsWrapperId="ad-groups-list"
        >
          {visitorGroupsLoading ? (
            skeletonArray.map((_, index) => (
              <TableRow id={`row-skeleton-${index}`} marginAfterContent key={`skeletonTableRowRightColumn-${_.id}`}>
                <TableCell isLoading firstLineText="" flex="0 0 100%" />
              </TableRow>
            ))
          ) : (
            <>
              <TableRow
                id="row-permission"
                key="row-permission"
                afterSwitchProps={{
                  checked: IsAdminGroup(leftTableSelectedRowGroupId) || false,
                  disabled: leftTableSelectedRowGroupId === undefined
                }}
                onClick={handleRightTableOnIsAdminRowClick}
                marginAfterContent
                disabled={leftTableSelectedRowGroupId === undefined}
              >
                <TableCell
                  firstLineText={translation.is_atlas_admin}
                  firstLineId="row-permission-firstLine"
                  secondLineText={translation.allows_users_in_this_group_to_configure_the_system_as_an_admin}
                  secondLineId="row-permission-second-line"
                  flex="0 0 100%"
                />
              </TableRow>
              {isAllowQrInvitesEnabled && (
                <TableRow
                  id="row-permission-inviter"
                  key="row-permission-inviter"
                  afterSwitchProps={{
                    checked: IsQrCodeVisitInviterGroup(leftTableSelectedRowGroupId) || false,
                    disabled: leftTableSelectedRowGroupId === undefined
                  }}
                  onClick={handleRightTableOnIsQrCodeVisitInviterRowClick}
                  marginAfterContent
                  disabled={leftTableSelectedRowGroupId === undefined}
                >
                  <TableCell
                    firstLineText={translation.allow_qr_invites}
                    firstLineId="row-permission-inviter-firstLine"
                    secondLineText={translation.allows_users_in_this_group_to_create_qr_code_invites}
                    secondLineId="row-permission-inviter-second-line"
                    flex="0 0 100%"
                  />
                </TableRow>
              )}
            </>
          )}
        </Table>
      </ComponentWrapper>
      <Drawer />
    </PageContent>
  );
};

export default Permissions;
